import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import type { ApuIaConcepto } from '../api/apuIa.js';
import { getPlanoRenglonApu, listarPlanoRenglones } from '../api/planos.js';
import { conceptoDeApuDeRenglon } from '../lib/apuDeRenglonDePlano.js';
import { ApuPrintDocumento, type PrintMode } from './QuoteApuIaPrintPage.js';

/**
 * Impresión de los análisis con IA de la hoja de un plano (mismo documento que la cotización).
 * Se abre en pestaña nueva desde «Imprimir análisis» de la hoja. Solo los renglones cuyo análisis
 * armó la IA y ya tienen precio, en el orden de la hoja.
 */
export function PlanoApuPrintPage() {
  const { id: projectId, fileId } = useParams<{ id: string; fileId: string }>();
  const [params] = useSearchParams();
  const mode: PrintMode = params.get('mode') === 'detallado' ? 'detallado' : 'simple';
  const [conceptos, setConceptos] = useState<ApuIaConcepto[] | null>(null);
  const [titulo, setTitulo] = useState('Hoja de planos');
  const [avance, setAvance] = useState<{ n: number; total: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!projectId || !fileId) return;
    let alive = true;
    (async () => {
      try {
        const { renglones } = await listarPlanoRenglones(projectId, fileId);
        const conIa = renglones.filter((r) => r.apuIaStatus === 'done' && r.precioUnitario != null);
        if (!alive) return;
        const nombre = conIa[0]?.sourceFileName ?? renglones[0]?.sourceFileName;
        if (nombre) setTitulo(`Hoja de planos · ${nombre}`);
        setAvance({ n: 0, total: conIa.length });
        // De a pocos a la vez: una hoja grande son decenas de análisis.
        const out: Array<ApuIaConcepto | null> = new Array(conIa.length).fill(null);
        let siguiente = 0;
        let listos = 0;
        const trabajador = async () => {
          while (alive && siguiente < conIa.length) {
            const k = siguiente++;
            const r = conIa[k]!;
            const apu = await getPlanoRenglonApu(projectId, r.sourceFileId ?? fileId, r.id).catch(() => null);
            if (apu?.matrix) {
              out[k] = conceptoDeApuDeRenglon(apu, {
                id: r.id, clave: r.resourceClave, cantidad: r.cantidad, importe: r.importe,
              });
            }
            listos++;
            if (alive) setAvance({ n: listos, total: conIa.length });
          }
        };
        await Promise.all(Array.from({ length: Math.min(6, conIa.length) }, trabajador));
        if (alive) setConceptos(out.filter((c): c is ApuIaConcepto => c != null));
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : 'No se pudo cargar el análisis');
      }
    })();
    return () => { alive = false; };
  }, [projectId, fileId]);

  if (error) return <div style={{ padding: 24, fontFamily: 'system-ui' }}>Error: {error}</div>;
  if (!conceptos) {
    return (
      <div style={{ padding: 24, fontFamily: 'system-ui' }}>
        Cargando análisis{avance && avance.total > 0 ? ` (${avance.n} de ${avance.total})` : ''}…
      </div>
    );
  }
  // La hoja de planos muestra sus importes en pesos; la impresión sigue el mismo criterio.
  return <ApuPrintDocumento titulo={titulo} moneda="MXN" mode={mode} conceptos={conceptos} />;
}
